import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Tooltip, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { CSSProperties, FC } from "react";
import { useTranslation } from "react-i18next";
import { EditTimePopover } from "../../../../../../../components/EditTimePopover";
import { SystemDropdown } from "../../../../../../../components/UIToolkit/Inputs/SystemDropDown";
import SystemButton from "../../../../../../../components/UIToolkit/SystemButton/SystemButton";
import { INK, NEUTRAL } from "../../../../../../../theme/tokens";
import type { Nurse } from "../../../../../../../utils/models";
import { inverseRolesMap } from "../../../../../../../utils/utilFunctions";
import { WarningDialog } from "../../../../../../../utils/warningDialogs/WarningDialog";
import { useSingleShiftCreation } from "./useSingleShiftCreation";

type Props = {
	nurseFromProfile: Nurse;
	selectedDate: DateTime;
	onCloseCalled: () => void;
};

const noLocationText = "No Location";
const typographyDefaultStyle: CSSProperties = {
	fontWeight: 500,
	fontSize: "14px",
	lineHeight: "100%",
	color: INK.SECONDARY,
};

export const SingleShiftCreationForm: FC<Props> = ({ nurseFromProfile, selectedDate, onCloseCalled }) => {
	const { t } = useTranslation();
	const {
		locations,
		onLocationDropdownChange,
		selectedLocation,
		editTimePickerClicked,
		startTimeValue,
		endTimeValue,
		shiftDurationString,
		timePickerAnchorEl,
		shiftTimes,
		handleTimePickerValueChange,
		addShiftClicked,
		isLoading,
		selectedJobId,
		setSelectedJobId,
		nurseJobs,
		warningState,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
		longShiftDuration,
		showLongShiftWarning,
		longShiftLimit,
	} = useSingleShiftCreation({
		noLocationText,
		selectedDate,
		nurseFromProfile,
		onCloseCalled,
	});

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				width: "100%",
				gap: 2,
				p: 1,
			}}
		>
			<SystemDropdown
				formControlSx={{
					width: "100%",
				}}
				data-testid="add-shift-location-select"
				label={"Select Location"}
				items={locations}
				valueToString={(value) => value?.location ?? noLocationText}
				maxMenuHeight={150}
				onChange={onLocationDropdownChange}
				selectedItem={selectedLocation}
			/>
			{nurseJobs.length > 0 && (
				<SystemDropdown
					formControlSx={{
						width: "100%",
					}}
					label={"Job"}
					items={nurseJobs.map((job) => ({ label: () => job.displayName, value: job.id, id: job.id }))}
					valueToString={(value) => nurseJobs.find((j) => j.id === value)?.displayName ?? ""}
					onChange={(value) => setSelectedJobId(value ?? undefined)}
					selectedItem={selectedJobId}
				/>
			)}
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "start",
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						border: (theme) => `1px solid ${showLongShiftWarning ? theme.palette.warning.main : NEUTRAL[300]}`,
						borderRadius: "8px",
						p: 1,
						height: "40px",
						justifyContent: "space-between",
						cursor: "pointer",
						gap: 1,
						bgcolor: showLongShiftWarning ? "warning.light" : "transparent",
					}}
					onClick={editTimePickerClicked}
					data-testid="add-shift-time-field"
				>
					<Typography>
						{startTimeValue ? startTimeValue : "--:--"} - {endTimeValue ? endTimeValue : "--:--"}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						{showLongShiftWarning && (
							<Tooltip title={t("shift.duration.warning", { hours: longShiftLimit })}>
								<WarningAmberIcon sx={{ fontSize: "16px", color: "warning.main" }} />
							</Tooltip>
						)}
						<AccessTimeIcon sx={{ fontSize: "16px" }} />
					</Box>
					<Typography style={{ color: "red" }}>*</Typography>
				</Box>
				<Typography
					style={typographyDefaultStyle}
					sx={{
						pt: 2,
					}}
				>
					{shiftDurationString}
				</Typography>
				{timePickerAnchorEl && (
					<EditTimePopover
						role={inverseRolesMap[nurseFromProfile.role]}
						jobId={selectedJobId}
						startTime={shiftTimes.startTime}
						endTime={shiftTimes.endTime}
						onTimeChange={handleTimePickerValueChange}
						onClose={editTimePickerClicked}
						anchorEl={timePickerAnchorEl}
						isActual={false}
					/>
				)}
			</Box>
			<WarningDialog
				openTargetWarning={warningState.isTargetWarningOpen}
				openPtoWarning={warningState.isPtoWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				openEmployeeStatusWarning={warningState.isEmployeeStatusWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				employeeStatusDetails={warningState.employeeStatusDetails}
				longShiftDuration={longShiftDuration}
				onCancelTarget={handleCancelWarnings}
				onConfirmTarget={handleTargetConfirm}
				onCancelPto={handleCancelWarnings}
				onConfirmPto={handlePtoConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
				onCancelEmployeeStatus={handleCancelWarnings}
				onConfirmEmployeeStatus={handleEmployeeStatusConfirm}
			/>
			<SystemButton data-testid="add-shift-submit-button" onClick={addShiftClicked} loading={isLoading}>
				Add Shift
			</SystemButton>
		</Box>
	);
};
